import api from '../services/api';
import { useAuthStore } from '../store/authStore';

/**
 * Generated images that came out of a PRIVATE Photo Booth file.
 *
 * A customer who marked what they sent as private asked for it — and what is made from it — to be deleted once it has been printed or
 * downloaded. The booth copy is handled by the booth itself (utils/boothPrivacy.js); this is the other half: the image the AI made.
 *
 *   registerPrivateResult(ref)   when such an image is made (the editor knows its input came from a private booth file)
 *   → the first download or print of it (imageGenerationService.trackDownload / trackPrint) calls schedulePrivateResultDelete(ref)
 *   → a minute later it is soft-deleted (hidden from the history exactly like the shop's own delete — nothing is erased from the books).
 *
 * The minute is for the download / print that triggered it: some of them still read the record for a moment afterwards. Both lists live in
 * localStorage, so a tab closed (or reloaded) in between still finishes the job — flushDueResultDeletes() runs on every start.
 */

const REGISTRY_KEY = 'sk_booth_private_results'; // refs of results made from a private file
const PENDING_KEY = 'sk_booth_private_pending'; // [{ ref, at }] waiting for their minute to pass
const GRACE_MS = 60 * 1000;
const RETRY_MS = 60 * 1000; // a delete that failed for a reason that may pass (offline, server hiccup) is tried again after this
const MAX_REMEMBERED = 200;

// Everything is mirrored in memory: when localStorage is blocked or full (a private window, a full disk) the lists still work for as
// long as this tab lives — they are just not carried over to the next visit.
const memory = new Map();
const volatile = new Set(); // keys whose last localStorage write failed — their memory copy is the newer one

const read = (key) => {
    if (volatile.has(key)) return memory.get(key) || [];
    try {
        const value = JSON.parse(localStorage.getItem(key) || '[]');
        return Array.isArray(value) ? value : [];
    } catch {
        return memory.get(key) || [];
    }
};
const write = (key, value) => {
    memory.set(key, value);
    try {
        localStorage.setItem(key, JSON.stringify(value));
        volatile.delete(key);
    } catch {
        volatile.add(key);
    }
};

let timer = null;
let flushing = false;

export function registerPrivateResult(ref) {
    if (ref == null || ref === '') return;
    const refs = read(REGISTRY_KEY).filter((r) => r !== String(ref));
    refs.push(String(ref));
    write(REGISTRY_KEY, refs.slice(-MAX_REMEMBERED));
}

export const isPrivateResult = (ref) => ref != null && read(REGISTRY_KEY).includes(String(ref));

/** The shop handed the result over some other way — shared it or copied it to paste elsewhere: the same moment as a download or a print. */
export function markPrivateResultDelivered(ref) {
    if (isPrivateResult(ref)) schedulePrivateResultDelete(ref);
}

/** Queue the image for deletion once its grace minute has passed (a second call for the same image changes nothing). */
export function schedulePrivateResultDelete(ref) {
    const key = String(ref);
    const pending = read(PENDING_KEY);
    if (pending.some((p) => p.ref === key)) return;
    write(PENDING_KEY, [...pending, { ref: key, at: Date.now() + GRACE_MS }]);
    write(REGISTRY_KEY, read(REGISTRY_KEY).filter((r) => r !== key));
    clearTimeout(timer);
    timer = setTimeout(() => { void flushDueResultDeletes(); }, GRACE_MS + 1000);
}

/**
 * Delete every queued image whose minute is up. One that is already gone — or was never this account's — is dropped; one that failed for a
 * reason that may pass (offline, signed out, a server hiccup) is tried again a minute later, not at once.
 */
export async function flushDueResultDeletes() {
    if (flushing) return;
    if (!useAuthStore.getState().token) return; // nobody is signed in: the next visit finishes it
    const now = Date.now();
    const due = read(PENDING_KEY).filter((item) => item.at <= now);
    if (due.length) {
        flushing = true;
        const finished = new Set(); // refs that are done (deleted, or not ours to delete)
        const retryAt = new Map(); // ref → when to try again
        try {
            for (const item of due) {
                try {
                    await api.delete(`/api/image-generation/delete/${encodeURIComponent(item.ref)}`);
                    finished.add(item.ref);
                } catch (err) {
                    // bad id / not found / gone / forbidden (another account's image): nothing more to do for this one
                    if ([400, 403, 404, 410].includes(err?.status)) finished.add(item.ref);
                    else retryAt.set(item.ref, Date.now() + RETRY_MS);
                }
            }
        } finally {
            // Re-read the list: an image scheduled WHILE the requests were in flight must survive (writing back the old copy would lose it).
            write(
                PENDING_KEY,
                read(PENDING_KEY)
                    .filter((item) => !finished.has(item.ref))
                    .map((item) => (retryAt.has(item.ref) ? { ...item, at: retryAt.get(item.ref) } : item)),
            );
            flushing = false;
        }
    }
    const next = read(PENDING_KEY).map((p) => p.at).sort((a, b) => a - b)[0];
    if (next) {
        clearTimeout(timer);
        timer = setTimeout(() => { void flushDueResultDeletes(); }, Math.max(next - Date.now(), 0) + 1000);
    }
}
